import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Linkedin, ExternalLink, ChevronDown, Zap, Bot, Lock } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading";

const innovationsList = [
  {
    title: "AI-Based Self-Healing Locator Framework",
    description: [
      "Implemented AI-based self-healing locator logic into the existing Selenium + Java automation framework at S&P Global.",
      "When a locator fails at runtime, the system intelligently identifies and switches to alternative element attributes to locate the element, preventing test failure.",
      "Reduced automation test failures caused by UI locator breakage by 60%, significantly improving test suite stability and reliability.",
      "Cut locator creation and maintenance effort by 80%, eliminating repetitive debugging cycles for the entire QA team.",
      "Integrated seamlessly into the existing Page Object Model (POM) architecture — no changes required to existing test scripts.",
    ],
    techStack: ["Java", "Selenium", "TestNG", "AI/ML", "POM", "S&P Global"],
    isInternal: true,
    icon: <Zap />,
  },
  {
    title: "AI-Driven Selenium UI Automation Agent",
    description: [
      "Developed an AI-driven automation workflow that analyzes existing Selenium-Java framework structure, coding patterns, reusable components, and framework rules before implementing test scenarios.",
      "Takes test steps as input and uses Playwright MCP to interact with the live application, understand UI behavior, identify required elements and their XPath/locator logic, and determine how the scenario should be automated.",
      "Analyzes the framework and test steps to identify reusable code, files to update, and new files required, then implements the changes following the Page Object Model (POM) architecture ensuring zero redundant or duplicate code is generated, all after user approval.",
      "Executes and validates generated scripts, with AI-assisted debugging and Playwright MCP-based investigation when failures occur, reducing manual test analysis, scripting, and debugging effort.",
    ],
    techStack: ["Selenium", "Java", "Playwright MCP", "Agentic AI", "S&P Global"],
    isInternal: true,
    icon: <Bot />,
  },
  {
    title: "Karate AI Agent - Autonomous Test Scripting",
    description: [
      "Built an AI-powered agent using GitHub Copilot (VS Code) that autonomously manages end-to-end Karate API test scenario creation and updates.",
      "The agent analyses existing feature files and step definitions, understands the full framework structure, identifies reusable code and methods, and generates a precise implementation plan - with zero redundant code.",
      "All framework rules, coding conventions, and AI guardrails are encoded in a structured .md instruction file - ensuring the agent always follows team standards and BDD best practices.",
      "Operates on a human-in-the-loop model: the agent presents its full plan and waits for confirmation before writing any code, iterating on feedback until approved.",
      "After test scripting, the agent autonomously runs the scenarios, detects failures, analyses the root cause, and self-corrects - completing a full autonomous test lifecycle from planning to execution.",
    ],
    techStack: ["Java", "Karate Framework", "GitHub Copilot", "VS Code", "Markdown", "BDD", "Agentic AI"],
    isInternal: true,
    icon: <Bot />,
  },
  {
    title: "Vibe Coding Portfolio",
    description: [
      "Built entire portfolio using AI models (GPT, Claude, Gemini) to generate code from well-defined prompts.",
      "Focused on core idea, layout, and UX while AI handled the implementation.",
      "Features dark/light mode with smooth theme switcher that saves preferences.",
      "Premium animations using Framer Motion for a polished feel.",
      "Fully responsive with content protection (no right-click or copy-paste).",
    ],
    techStack: ["React", "Tailwind CSS", "Framer Motion", "GPT", "Claude", "Gemini"],
    link: "https://www.linkedin.com/posts/gulshan98_vibecoding-ai-gpt-activity-7405796190140874752-M64A",
    icon: <Linkedin />,
  },
  {
    title: "YouTube SDET Notifier",
    description: [
      "Telegram bot that tracks new videos from top testing & automation creators.",
      "Uses GPT-based AI to generate quick summaries so you can decide whether to watch.",
      "Sends clean, concise notifications on schedule (Wed & Sat at 9 PM IST).",
      "Currently tracking: ExecuteAutomation, Naveen Automation Labs, Rahul Shetty Academy.",
      "Automated via GitHub Actions for hands-free operation.",
    ],
    techStack: ["Java", "YouTube API", "GPT", "Telegram Bot API", "GitHub Actions"],
    link: "https://www.linkedin.com/posts/gulshan98_sdet-automationtesting-qacommunity-activity-7418525106789920768-hlQq",
    icon: <Linkedin />,
  },
  {
    title: "AI-Powered Job Search Tool",
    description: [
      "Automatically searches jobs from Apna.co using Selenium (Java + TestNG).",
      "Sends each job description to GPT-4.1 (via Azure) to compare with resume.",
      "Returns a match score out of 100 and filters best matches above threshold.",
      "Saves matched jobs with title, link, and score in an Excel sheet.",
      "Sends results to Telegram via bot for instant notifications.",
    ],
    techStack: ["Selenium", "Java", "TestNG", "GPT-4.1", "Azure API", "Apache POI", "Telegram Bot"],
    link: "https://www.linkedin.com/posts/gulshan98_selenium-testautomation-openai-activity-7355786663215722497-JYQy",
    icon: <Linkedin />,
  },
  {
    title: "JMeter Automated Setup",
    description: [
      "Automates entire JMeter setup process - from download to desktop shortcut creation.",
      "Always downloads the latest version automatically.",
      "Extracts ZIP to configurable project folder and cleans up after.",
      "Creates desktop shortcut for jmeter.bat using VBScript.",
      "Saves 10-20 minutes per new team member onboarding.",
    ],
    techStack: ["Selenium", "Java", "VBScript", "FluentWait"],
    link: "https://www.linkedin.com/posts/gulshan98_selenium-java-jmeter-activity-7355424278856544258-vwYM",
    icon: <Linkedin />,
  },
];

const InnovationCard = ({ innovation, index, isOpen, onToggle }) => (
  <motion.div
    layout
    transition={{ duration: 0.3, ease: "easeInOut" }}
    className={`relative rounded-xl border overflow-hidden transition-all duration-300 shadow-sm
      ${isOpen
        ? "border-blue-400 dark:border-blue-500 shadow-blue-100 dark:shadow-blue-900/20 shadow-md"
        : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md"
      }
      bg-white dark:bg-gray-800
    `}
  >
    {/* Header */}
    <div
      className="group flex items-start justify-between gap-4 pl-5 pr-4 py-4 cursor-pointer"
      onClick={() => onToggle(index)}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <div className={`flex items-center font-semibold space-x-2 transition-colors duration-300 ${isOpen ? "text-blue-700 dark:text-blue-400" : "text-gray-800 dark:text-gray-200"}`}>
          <span className={`text-xl flex-shrink-0 transition-colors duration-300 ${isOpen ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400"}`}>
            {innovation.icon}
          </span>
          <span className="text-sm sm:text-base leading-snug">{innovation.title}</span>
        </div>
      </div>

      {/* Chevron pin to top right */}
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className={`flex-shrink-0 mt-0.5 transition-colors duration-300 ${isOpen ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500 group-hover:text-blue-400"}`}
      >
        <ChevronDown size={20} />
      </motion.div>
    </div>

    {/* Expandable content */}
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          {/* Subtle top divider */}
          <div className="mx-4 border-t border-gray-100 dark:border-gray-700" />

          {/* Expanded body block */}
          <div className="pl-5 pr-4 pb-5 pt-3 bg-blue-50/30 dark:bg-blue-900/10">
            <ul className="space-y-2.5">
              {innovation.description.map((point, idx) => (
                <motion.li
                  key={`${index}-${idx}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                  className="flex items-start gap-3 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed"
                >
                  <span className="flex-shrink-0 mt-2.5 w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                  <span>{point}</span>
                </motion.li>
              ))}
            </ul>

            {/* Tech Stack */}
            <div className="mt-4 flex flex-wrap gap-2">
              {innovation.techStack.map((tech, idx) => (
                <motion.span
                  key={idx}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                  className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 transition-colors duration-300"
                >
                  {tech}
                </motion.span>
              ))}
            </div>

            {/* Link or Internal Badge */}
            <div className="mt-4">
              {innovation.isInternal ? (
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600">
                  <Lock size={12} />
                  Internal Company Project
                </span>
              ) : (
                <a
                  href={innovation.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium inline-flex items-center transition-colors duration-300"
                >
                  View on LinkedIn
                  <ExternalLink className="ml-2" size={16} />
                </a>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const Innovations = () => {
  const [expandedIndex, setExpandedIndex] = useState(0);

  const toggleInnovation = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section
      id="innovations"
      className="pt-4 pb-10 px-4 sm:px-6 md:px-16 bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      <div className="container mx-auto max-w-screen-xl">
        <SectionHeading
          title="Innovations"
          subtitle="Side projects & creative experiments"
          gradient="blue"
        />

        <div className="space-y-4">
          {innovationsList.map((innovation, index) => (
            <InnovationCard
              key={index}
              innovation={innovation}
              index={index}
              isOpen={expandedIndex === index}
              onToggle={toggleInnovation}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Innovations;
